import React, { Component } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import {
  indexOf, defer, delay, each, map, find, first, startsWith, debounce, reduce,
} from 'lodash';
import ReactResizeDetector from 'react-resize-detector';
import { connect } from 'react-redux';
import Fa from 'react-fontawesome';
import { AgGridReact } from 'ag-grid-react';
import { Modal, Button, Tooltip, OverlayTrigger, Dropdown, MenuItem } from 'react-bootstrap';
import { toast } from 'react-toastify';

import CustomHeader from '../../../../components/god-table/header';
import SearchField from '../../../../components/search-field';
import OrganizationUserButton from '../OrganizationUserButton/OrganizationUserButton';
import SaleRepsNameRenderer from '../../../../components/table-cells/SaleRepsNameRenderer';
import EmptyCellRenderer from '../../../../components/table-cells/EmptyCellRenderer';
import DateRenderer from '../../../../components/table-cells/DateRenderer';
import NumberRenderer from '../../../../components/table-cells/NumberRenderer';
import ActionRenderer from './ActionRenderer';
import OrganizationNameRenderer from '../../../../components/table-cells/OrganizationNameRenderer';
import LicenseRenderer from './LicenseRenderer';
import IntegrationUserRenderer from './IntegrationUserRenderer';
import Veil from '../../../../components/god-table/veil';
import FeedbackRequestRenderer from './FeedbackRequestRenderer';
import { getEmailSignatures } from '../../../../components/request-signature/reducer';
import EmailSignatureRenderer from './EmailSignatureRenderer';
import BrowserRenderer from './BrowserRenderer';
import DeviceRenderer from './DeviceRenderer';
import CheckSyncPermissionModal from './CheckSyncPermissionModal';
import SearchForm from '../../../../components/search-form';

import { getViewableDimensions } from '../../../App/AppReducer';
import {
  requestDeleteOrganizationUser,
  requestInviteOrganizationUser,
  requestDisconnectOrganizationUser,
  requestSyncOwnerProfile,
} from '../../SetupActions';
import { requestHijack } from '../../../Auth/AuthActions';
import {
  getUser,
  getIsSuperAdmin,
  getIsAdmin,
  getIsPIUser,
  getIsOrganizationAdmin,
} from '../../../Auth/AuthReducer';

import styles from './styles.css';

const organizationRoles = {
  admin: 'Admin',
  manager: 'Manager',
  user: 'Sales Rep',
};

function GroupRowInnerRenderer(props) {
  const { value } = props;
  if (!value) {
    return null;
  }
  let display = value;
  if (value === true || value === 'true') {
    display = (
      <span
        className='label label-success'
        style={{ display: 'inline-block', fontSize: '12px' }}
      >
        With Active License
      </span>
    );
  } else if (value === false || value === 'false') {
    display = (
      <span
        className='label label-danger'
        style={{ display: 'inline-block', fontSize: '12px' }}
      >
        With Inactive License
      </span>
    );
  }
  return <strong className='text-uppercase'>{display}</strong>;
}
GroupRowInnerRenderer.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
};
GroupRowInnerRenderer.defaultProps = {
  value: null,
};

function SFRenderer(props) {
  const { data } = props;
  if (!data) {
    return null;
  }
  const value = data.is_none_standard_user;
  const name = data.display_name || data.name;
  const isSF = !value;
  let cls = 'text-muted';
  let fa = isSF ? 'circle-o' : 'bolt';
  const connected =
    data.last_activity !== null && data.last_activity !== undefined;
  let msg = isSF
    ? 'will need to login via CRM Connect'
    : 'will need to login using email and password';

  if (connected) {
    cls = isSF ? 'text-success' : 'text-warning';
    fa = isSF ? 'circle' : 'bolt';
    msg = isSF
      ? 'has already logged in via CRM Connect'
      : 'has already logged in using email and password';
  }

  const tip = (
    <Tooltip id={`sf-connect-${data._id}`}>
      <div>
        {name} {msg}
      </div>
    </Tooltip>
  );
  return (
    <div>
      <OverlayTrigger
        trigger={['hover', 'focus']}
        placement='right'
        overlay={tip}
      >
        <Fa name={fa} className={cls} />
      </OverlayTrigger>
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
      <SaleRepsNameRenderer {...props} />
    </div>
  );
}
SFRenderer.propTypes = {
  data: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    display_name: PropTypes.string,
    last_activity: PropTypes.string,
    is_none_standard_user: PropTypes.bool,
  }),
  value: PropTypes.string,
};
SFRenderer.defaultProps = {
  data: null,
  value: null,
};

function SFUserIdRenderer(props) {
  const { data, value } = props;
  if (!data || !value) {
    return null;
  }

  return <code>{value}</code>;
}
SFUserIdRenderer.propTypes = {
  data: PropTypes.shape({
    _id: PropTypes.string,
  }),
  value: PropTypes.string,
};
SFUserIdRenderer.defaultProps = {
  data: null,
  value: null,
};

class UserTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      deleting: {},
      inviting: {},
      editing: {},
      syncing: {},
      error: null,
      hijacking: false,
      hijackedUser: null,
      showModal: false,
      copyUserId: null,
      hasUnselected: false,
      editingUser: null,
      statusFilter: props.statusFilter,
      licenseFilter: props.licenseFilter,
      fullScreenMode: false,
      topBarHeight: 0,
      currentTotal: null,
      fieldQuery: null,
      formQuery: null,
      selectedAll: false,
      userCheckSyncPermission: null,
      showCheckSyncPermissionModal: false,
      showUserTable: !(props.showSearchForm === true), // if search form shown, show table after first filter application
    };
    this.close = this.close.bind(this);
    this.open = this.open.bind(this);
    this.handleInvite = this.handleInvite.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.hijack = this.hijack.bind(this);
    this.done = this.done.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.onFirstDataRendered = this.onFirstDataRendered.bind(this);
    this.handleSearchFieldChange = debounce(this.handleSearchFieldChange.bind(this), 300, {});
    this.handleSearchFormChange = this.handleSearchFormChange.bind(this);
    this.handleSearchFormLoadError = this.handleSearchFormLoadError.bind(this);
    this.lastLoginColumnCellRendererSelector = this.lastLoginColumnCellRendererSelector.bind(this);
    this.nameColumnCellRendererSelector = this.nameColumnCellRendererSelector.bind(this);
    this.loginCountColumnCellRendererSelector = this.loginCountColumnCellRendererSelector.bind(this);
    this.onSelectionChanged = this.onSelectionChanged.bind(this);
    this.actionColumnCellRendererSelector = this.actionColumnCellRendererSelector.bind(this);
    this.onCopy = this.onCopy.bind(this);
    this.requestEdit = this.requestEdit.bind(this);
    this.setStatus = this.setStatus.bind(this);
    this.actionColumnValueGetter = this.actionColumnValueGetter.bind(this);
    this.toggleFullScreenMode = this.toggleFullScreenMode.bind(this);
    this.onResizeTopBar = this.onResizeTopBar.bind(this);
    this.handleDownload = this.handleDownload.bind(this);
    this.getRows = this.getRows.bind(this);
    this.handleUserUpdated = this.handleUserUpdated.bind(this);
    this.userIdColumnCellRendererSelector = this.userIdColumnCellRendererSelector.bind(this);
    this.handleUserAdded = this.handleUserAdded.bind(this);
    this.feedbackRequestColumnCellRendererSelector = this.feedbackRequestColumnCellRendererSelector.bind(this);
    this.handleRequestDisconnect = this.handleRequestDisconnect.bind(this);
    this.emailSignatureColumnValueGetter = this.emailSignatureColumnValueGetter.bind(this);
    this.handleSignatureSent = this.handleSignatureSent.bind(this);
    this.getDeviceValue = this.getDeviceValue.bind(this);
    this.getBrowserValue = this.getBrowserValue.bind(this);
    this.onSelectedAllCheckbox = this.onSelectedAllCheckbox.bind(this);
    this.isIntegrationUserGetter = this.isIntegrationUserGetter.bind(this);
    this.getRowClass = this.getRowClass.bind(this);
    this.handleRequestSyncOwnerProfile = this.handleRequestSyncOwnerProfile.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.showHijack !== this.props.showHijack && this.gridApi) {
      this.refresh();
      if (this.props.showHijack) {
        this.context.addNotification(<div>Hijack mode enabled!</div>, {
          type: 'warning',
          autoClose: false,
        });
      }
    }

    if (
      this.props.selectedIds &&
      prevProps.selectedIds.length > 0 &&
      this.props.selectedIds.length === 0
    ) {
      this.syncSelectedIds(this.props.selectedIds);
    }

    const currentRow = prevProps.flashRow || {};
    if (this.props.flashRow && this.props.flashRow._id !== currentRow._id) {
      this.flashRow(this.props.flashRow);
    }
  }

  getRowClass(params) {
    if (!params || !params.data) {
      return null;
    }
    if (params.data.active !== true) {
      return styles.rowInactive;
    }
    if (params.data.disable_feedback_request === true) {
      return styles.rowDanger;
    }
    return null;
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    const createDatasource = () => ({
      // called by the grid when more rows are required
      getRows: this.getRows,
    });
    const myDatasource = createDatasource();
    params.api.setServerSideDatasource(myDatasource);
  }

  onSelectedAllCheckbox(selectedAll) {
    this.props.onSelectedAllCheckbox(selectedAll);
    this.setState({ selectedAll }, () => {
      this.syncSelectedIds(this.props.selectedIds);
    });
  }

  onFirstDataRendered() {
    if (this.gridApi && this.gridColumnApi) {
      this.gridApi.showLoadingOverlay();
      delay(() => {
        const { viewableDimensions } = this.props;
        const allColumns = this.gridColumnApi.getColumns();
        const allColumnIds = map(allColumns, 'colId');
        this.gridColumnApi.autoSizeColumns(allColumnIds);
        let allWidths = 0;
        each(this.gridColumnApi.getColumns(), (col) => {
          allWidths += col.actualWidth;
        });
        const viewableWidth = viewableDimensions.width;
        if (allWidths < viewableWidth - 2) {
          this.gridApi.sizeColumnsToFit();
        }
        this.gridApi.hideOverlay();
      }, 500);
    }
  }

  onCopy(user) {
    this.setState({ copyUserId: user._id });
    delay(() => {
      this.setState({ copyUserId: null });
    }, 2000);
    this.context.addNotification(
      <div>
        {user.display_name}&rsquo;s invitation link was copied to your
        clipboard!
      </div>,
    );
  }

  onSelectionChanged() {
    const selectedRows = [];
    const unselectedRows = [];

    this.gridApi.forEachNode((node) => {
      const nodeData = node.data;
      if (nodeData) {
        if (node.selected) {
          selectedRows.push(nodeData);
        } else {
          unselectedRows.push(nodeData);
        }
      }
    });

    this.setState({ hasUnselected: unselectedRows.length > 0 });

    const requestQuery = this.gridApi ? this.parseRequest(
      this.gridApi.serverSideRowModel.cacheParams,
    ) : {};
    this.props.toggleSelect(selectedRows, unselectedRows, requestQuery);
  }

  onResizeTopBar(w, topBarHeight) {
    this.setState({ topBarHeight });
  }

  close() {
    this.setState({ showModal: false, error: null });
  }

  getDeviceValue(params) {
    if (!params || !params.data || !params.data.user_agent || !params.data.user_agent.device) {
      return '';
    }
    const { device } = params.data.user_agent;
    const s = `${device.brand} ${device.type}`;
    if (params.data.user_agent.os) {
      return `${s} (${params.data.user_agent.os.name} ${params.data.user_agent.os.version})`;
    }
    return s;
  }

  getBrowserValue(params) {
    if (!params || !params.data || !params.data.user_agent || !params.data.user_agent.client) {
      return '';
    }
    const { client } = params.data.user_agent;

    const s = `${client.name} ${client.version}`;
    return s;
  }

  getColumnField(colId) {
    let sortKey = null;
    if (colId === 'LICENSE') {
      sortKey = 'active';
    } else if (colId === 'ROLE') {
      sortKey = 'organization_role';
    } else if (colId === 'SF_ROLE') {
      sortKey = 'user_role_name';
    } else if (colId === 'SF_PROFILE') {
      sortKey = 'profile_name';
    } else if (colId === 'EMAIL') {
      sortKey = 'email';
    } else if (colId === 'LAST_LOGIN') {
      sortKey = 'last_activity';
    } else if (colId === 'LOGIN_COUNT') {
      sortKey = 'logs';
    } else if (colId === 'SF_ID') {
      sortKey = 'user_id';
    }
    return sortKey;
  }

  parseRequest(request) {
    if (!request) {
      request = this.state.request;
    } else {
      this.setState({ request });
    }
    const { fieldQuery, formQuery, licenseFilter, statusFilter } = this.state;
    const { showSearchForm } = this.props;

    const extraFilter = this.props.filter;

    const query = {
      ...extraFilter,
      startRow: request.startRow,
      endRow: request.endRow,
    };

    if (request.sortModel && request.sortModel.length) {
      const firstSort = first(request.sortModel);
      if (firstSort) {
        const { colId, sort } = firstSort;
        const sortKey = this.getColumnField(colId);
        if (sortKey) {
          query.sort = sortKey;
          query.sortDir = sort;
        }
      }
    }

    if (request.filterModel) {
      const filterModel = reduce(request.filterModel, (items, v, k) => {
        const ky = this.getColumnField(k);

        const cond = {};
        if (v.operator === 'AND' || v.operator === 'OR') {
          // multiple
          const logic = v.operator === 'AND' ? '$and' : '$or';
          cond[logic] = [];
          each(v, (val, con) => {
            if (startsWith(con, 'condition')) {
              cond[logic].push({
                type: val.type,
                filter: val.filter || val.dateFrom,
              });
            }
          });
        } else {
          // single
          cond.$and = [{ type: v.type, filter: v.filter || v.dateFrom }];
        }
        items[ky] = cond;
        return items;
      }, {});
      query.filter = JSON.stringify(filterModel);
    }

    if (fieldQuery) {
      query.q = fieldQuery;
    }
    
    if (showSearchForm && formQuery) {
      query.ownerQuery = JSON.stringify(formQuery);
    }
  
    if (licenseFilter !== null) {
      query.license = licenseFilter === true;
    } else {
      delete query.license;
    }
  
    if (statusFilter !== null) {
      query.connected = statusFilter === true;
    }  else {
      delete query.connected;
    }
  
    return query;
  }

  getRows(params) {
    const { request } = params;
    console.log('params', params)
    const query = this.parseRequest(request);
    this.props.fetchAction(query).then(
      (response) => {
        const { users: { items, meta: { pagination } } } = response;
        console.log('itemsitemsitems', items)
        if (pagination && pagination.total !== undefined) {
          this.setState({ currentTotal: pagination.total }, () => {
            params.success({ rowData: items });
          });
        } else {
          // params.success(items, this.state.currentTotal);
          params.success({ rowData: items });
        }
        const { selectedIds } = this.props;
        this.syncSelectedIds(selectedIds);
      },
      (errors) => {
        params.fail(errors);
      },
    );
  }

  open() {
    this.setState({ showModal: true });
  }

  isIntegrationUserGetter(params) {
    if (!params || !params.data) {
      return null;
    }
    if (this.props.organization && this.props.organization.type === 'azure') {
      return null;
    }
    if (params.data.is_none_standard_user === true) {
      return null;
    }
    if (params.data.user_id && params.data.is_synchronization_user) {
      return true;
    }
    return false;
  }

  syncSelectedIds(selectedIds) {
    if (this.gridApi) {
      if (this.state.selectedAll || selectedIds.length > 0) {
        this.gridApi.forEachNode((node) => {
          const nodeData = node.data;
          if (nodeData) {
            node.setSelected(this.state.selectedAll || indexOf(selectedIds, nodeData._id) >= 0);
          } else {
            node.setSelected(false);
          }
        });
      } else {
        this.gridApi.deselectAll();
      }
    }
    this.onFirstDataRendered();
  }

  toggleFullScreenMode() {
    this.setState({ fullScreenMode: !this.state.fullScreenMode }, () => {});
  }

  handleSignatureSent(user) {
    this.handleUserUpdated(user);
    this.context.addNotification(
      <div>
        A request email has been sent to <strong>{user.display_name}</strong> at{' '}
        <strong>{user.email}</strong>!
      </div>,
      { type: 'success' },
    );
  }

  handleRequestSyncOwnerProfile(user) {
    const { organization } = this.props;

    const id = user._id;
    if (!this.state.syncing[id]) {
      this.setState({
        syncing: {
          ...this.state.syncing,
          [id]: true,
        },
      }, () => {
        let toastId = null;

        const toasterClose = () => {
          toastId = null;
        }

        const el = (<div><Fa name="spinner" spin /> Syncing owner profile for <strong>{user.first_name} {user.last_name}</strong></div>);

        toastId = this.context.addNotification(el, { type: 'warning', autoClose: false, onClose: toasterClose });

        this.props.dispatch(requestSyncOwnerProfile(organization._id, id))
          .then(
            () => {
              this.setState({
                syncing: {
                  ...this.state.syncing,
                  [id]: false,
                },
              }, () => {
                const el1 = (<div>Syncing owner profile for <strong>{user.first_name} {user.last_name}</strong> success!</div>);
                if (toastId) {
                  toast.update(toastId, { 
                    render: () => el1,
                    type: 'success',
                    autoClose: true,
                    onClose: toasterClose,
                  });
                } else {
                  toastId = this.context.addNotification(el, { type: 'success', autoClose: true, onClose: toasterClose });
                }
              });
            },
            (e) => {
              this.setState({
                syncing: {
                  ...this.state.syncing,
                  [id]: false,
                },
              }, () => {
                const el1 = (<div>Syncing owner profile for <strong>{user.first_name} {user.last_name}</strong> error!</div>);
                if (toastId) {
                  toast.update(toastId, { 
                    render: () => el1,
                    type: 'error',
                    autoClose: true,
                    onClose: toasterClose,
                  });
                } else {
                  toastId = this.context.addNotification(el, { type: 'success', autoClose: true, onClose: toasterClose });
                }
              });
            },
          );
      });
    }
  }

  handleDownload() {
    if (this.gridApi) {
      const { organization, loginUser } = this.props;
      /*
      // front end export
      const columnKeys = without(map(this.gridColumnApi.getColumns(), 'colId'), 'ACTIONS', 'LOGIN_ONCE');
      const params = {
        columnGroups: true,
        fileName: 'Users - TruSales',
        sheetName: 'Users',
        exportMode: 'xlsx',
        columnKeys,
      };
      this.gridApi.exportDataAsExcel(params);
      */

      // backend export
      const params = this.parseRequest(
        this.gridApi.serverSideRowModel.cacheParams,
      );
      delete params.startRow;
      delete params.endRow;
      params.token = loginUser.$token;
      const url = `/api/organizations/${
        organization._id
      }/users/download?${qs.stringify(params)}`;
      window.open(url);
    }
  }

  handleUserUpdated(user) {
    if (this.gridApi) {
      const node = this.gridApi.getRowNode(user._id);
      if (node) {
        node.setData(user);
        this.gridApi.redrawRows({ rowNodes: [node], force: true, suppressFlash: true });
        this.flashRow(user);
      }
    }
  }

  purgeServerSideCache() {
    if (this.gridApi) {
      // this.gridApi.purgeServerSideCache();
      this.gridApi.refreshServerSide({ route: [], purge: true });
    }
  }

  handleUserAdded(user) {
    this.props.onAdd(user);
    if (this.gridApi) {
      this.setState({ fieldQuery: null }, () => {
        this.purgeServerSideCache();
      });
    }
  }

  flashRow(rowData) {
    if (this.gridApi) {
      const node = this.gridApi.getRowNode(rowData._id);
      if (node) {
        this.gridApi.flashCells({ rowNodes: [node] });
      }
    }
  }

  refresh(rowData) {
    if (this.gridApi) {
      if (!rowData) {
        this.gridApi.showLoadingOverlay();
        this.gridApi.redrawRows({ force: true });
        this.gridApi.hideOverlay();
      } else {
        let node = null;
        this.gridApi.forEachNode((n) => {
          if (!node && n.data._id === rowData._id) {
            node = n;
          }
        });
        if (node) {
          this.gridApi.showLoadingOverlay();
          this.gridApi.refreshCells({ rowNodes: [node], force: true });
          this.gridApi.hideOverlay();
        }
      }
    }
  }

  handleSearchFieldChange(query) {
    if (this.gridApi) {
      this.setState({ fieldQuery: query }, () => {
        this.purgeServerSideCache();
      });
    }
  }

  handleSearchFormChange(criteria) {
    // show table once search is applied, not on first load
    const filterApplied = this.state.showUserTable === false && this.state.formQuery != null;
    const { query } = criteria;
    this.setState({
      formQuery: query,
      ...(filterApplied ? { showUserTable: true } : {}),
    }, () => {
      this.purgeServerSideCache();
    });
  }

  handleSearchFormLoadError(_error) {
    this.context.addNotification((<div>Error loading owner search form.</div>), { type: 'error', autoClose: true });
    this.setState({ showUserTable: true });
  }

  handleInvite(user) {
    const { inviting } = this.state;
    const id = user._id;
    this.setState({ inviting: { ...inviting, [id]: true } });
    this.props.dispatch(requestInviteOrganizationUser(user.organization__id, user._id))
      .then(
        () => {
          this.props.onInvite(user);
          this.setState({ inviting: { ...inviting, [id]: false } });
          this.context.addNotification(
            <div>
              The invitation email for {user.display_name} is processing!
            </div>,
          );
          this.refresh(user);
        },
        (error) => {
          this.setState({
            inviting: { ...inviting, [id]: false },
            error: error.data || error,
          });
          this.open();
        },
      );
  }

  requestEdit(editingUser) {
    this.setState({ editingUser });
  }

  
  handleRequestDisconnect(user) {
    const { editing } = this.state;
    const id = user._id;
    this.setState({ editing: { ...editing, [id]: true } });
    this.props.dispatch(requestDisconnectOrganizationUser(user.organization__id, user._id))
      .then(
        () => {
          this.props.onDelete(user);
          this.purgeServerSideCache();
          this.setState({ deleting: { ...editing, [id]: false } });
        },
        (error) => {
          this.setState({
            deleting: { ...editing, [id]: false },
            error: error.data || error,
          });
          this.open();
        },
      );
  }

  handleDelete(user) {
    const { deleting } = this.state;
    const id = user._id;
    this.setState({ deleting: { ...deleting, [id]: true } });
    this.props.dispatch(requestDeleteOrganizationUser(user.organization__id, user._id))
      .then(
        () => {
          this.props.onDelete(user);
          this.purgeServerSideCache();
          this.setState({ deleting: { ...deleting, [id]: false } });
        },
        (error) => {
          this.setState({
            deleting: { ...deleting, [id]: false },
            error: error.data || error,
          });
          this.open();
        },
      );
  }

  hijack(user) {
    this.setState({ hijacking: true, hijackedUser: user });
    this.props.dispatch(requestHijack(user._id))
      .then(
        (newUser) => {
          this.setState({ hijacking: false, hijackedUser: newUser });
        },
        () => {
          this.setState({ hijacking: false, hijackedUser: null });
        },
      );
  }

  done() {
    const { hijackedUser } = this.state;
    this.setState({ hijackedUser: null });
    if (hijackedUser.role !== 'admin' && hijackedUser.role !== 'superadmin') {
      defer(this.context.router.history.push, '/');
    }
  }

  userIdColumnCellRendererSelector(params) {
    if (!params || !params.data) {
      return null;
    }
    if (params.data.is_none_standard_user === true) {
      return {
        component: EmptyCellRenderer,
      };
    }
    return {
      params: {},
      component: SFUserIdRenderer,
    };
  }

  feedbackRequestColumnCellRendererSelector(params) {
    if (!params || !params.data) {
      return null;
    }
    return {
      params: {},
      component: FeedbackRequestRenderer,
    };
  }

  lastLoginColumnCellRendererSelector(params) {
    if (!params) {
      return null;
    }
    const { value, data } = params;
    if (!data || !data._id) {
      return null;
    }
    if (value) {
      return {
        params: {
          format: 'shortWithTime',
          relative: true,
        },
        component: DateRenderer,
      };
    }
    return {
      component: EmptyCellRenderer,
    };
  }

  nameColumnCellRendererSelector(params) {
    if (!params || !params.data) {
      return null;
    }
    const { detailUrl } = this.props;
    const { value } = params;
    const { role } = params.data;
    let url = null;
    if (role === 'pi' && detailUrl) {
      url = `${detailUrl}${params.data._id}`;
    }
    if (value) {
      return {
        params: {
          isOwner: true,
          detailUrl: url,
          showAvatar: true,
        },
        component: SFRenderer,
      };
    }
    return {
      component: EmptyCellRenderer,
    };
  }

  actionColumnCellRendererSelector(params) {
    if (!params || !params.data) {
      return null;
    }
    const {
      showHijack,
      isSuperAdmin,
      isOrganizationAdmin,
      showEmailColumn,
      isPIUser,
      loginUser,
      isAdmin,
    } = this.props;
    const {
      deleting,
      editing,
      inviting,
      hijackedUser,
      copyUserId,
    } = this.state;
    const user = params.data;
    const targetUserRole = user.role;

    const hijackingId = hijackedUser ? hijackedUser._id : null;

    let canCopyInvitationLink = false;
    let copying = false;

    if (
      (isSuperAdmin || isOrganizationAdmin) &&
      user.last_activity === null &&
      user.invite_url !== null &&
      user.invite_url !== undefined &&
      !user.is_none_standard_user
    ) {
      canCopyInvitationLink = true;
      copying = copyUserId === user._id;
    }

    let canInvite = false;
    if (user.last_activity === null && user.logs === 0) {
      canInvite = true;
    }

    let canEdit = false;
    let canDelete = false;
    let canDisconnect = false;
    if (!showEmailColumn) {
      if (isSuperAdmin) {
        canEdit = true;
        canDelete = canEdit;
      } else if (isAdmin) {
        canEdit = targetUserRole !== 'superadmin';
        canDelete = canEdit;
      } else if (isPIUser) {
        canEdit = targetUserRole !== 'superadmin' && targetUserRole !== 'admin';
        canDelete = canEdit;
      } else if (
        isOrganizationAdmin &&
        user.organization__id === loginUser.organization__id
      ) {
        canEdit =
          (targetUserRole !== 'superadmin' && targetUserRole !== 'admin') ||
          user._id === loginUser._id;
        canDelete = canEdit;
      }
      canDisconnect =
        canDelete &&
        canEdit &&
        user.last_activity &&
        user.logs > 0 &&
        !user.is_none_standard_user;
      if (user._id === loginUser._id) {
        canDelete = false;
        canDisconnect = false;
      }
    }

    return {
      params: {
        canEdit,
        editing: editing[user._id],
        requestEdit: this.requestEdit,

        canDisconnect,
        requestDisconnect: this.handleRequestDisconnect,

        canDelete,
        deleting: deleting[user._id],
        requestDelete: this.handleDelete,

        canEmail: showEmailColumn,

        canHijack: showHijack && isSuperAdmin,
        hijacking: hijackingId === user._id,
        requestHijack: this.hijack,

        canCopyInvitationLink,
        copying,
        requestCopyInvitationLink: this.onCopy,

        canInvite,
        inviting: inviting[user._id],
        requestInvite: this.handleInvite,

        canSyncOwnerProfile: user.user_id !== undefined && user.user_id !== null,
        requestSyncOwnerProfile: this.handleRequestSyncOwnerProfile,
      },
      component: ActionRenderer,
    };
  }

  emailSignatureColumnValueGetter(params) {
    if (!params || !params.data) {
      return null;
    }
    const { emailSignatures } = this.props;
    const { email } = params.data;
    const signature = emailSignatures[email] || {};
    return signature.signature;
  }

  actionColumnValueGetter(params) {
    if (!params || !params.data) {
      return null;
    }

    const { deleting, editing, inviting } = this.state;
    const user = params.data;
    const userEditing = editing[user._id] ? 1 : 0;
    const userDeleting = deleting[user._id] ? 1 : 0;
    const userInviting = inviting[user._id] ? 1 : 0;
    const userPending = user.pending_invite === true ? 1 : 0;
    return `${userEditing}|${userDeleting}|${userInviting}|${userPending}`;
  }

  loginCountColumnCellRendererSelector(params) {
    if (!params) {
      return null;
    }
    const { value, data } = params;
    if (!data || !data._id) {
      return null;
    }
    if (value && data && data.last_activity) {
      return {
        component: NumberRenderer,
      };
    }
    return {
      component: EmptyCellRenderer,
    };
  }

  setStatus(statusFilter) {
    this.setState({ statusFilter }, () => {
      this.purgeServerSideCache();
    });
  }

  setLicense(licenseFilter) {
    this.setState({ licenseFilter }, () => {
      this.purgeServerSideCache();
    });
  }

  renderModal() {
    const { error, showModal } = this.state;
    return (
      <Modal show={showModal} onHide={this.close}>
        <Modal.Header closeButton>
          <Modal.Title className='text-danger'>Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error !== null && (
            <p className='text-danger'>
              <Fa name='hand-stop-o' /> {error.message}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button bsStyle='primary' onClick={this.close}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  renderCheckPermissionModal() {
    const { userCheckSyncPermission } = this.state;
    if (!userCheckSyncPermission) {
      return null;
    }
    return (
      <CheckSyncPermissionModal
        show={this.state.showCheckSyncPermissionModal}
        onClose={() => { this.setState({ showCheckSyncPermissionModal: false }); }}
        user={userCheckSyncPermission}
        organization={this.props.organization}
      />
    );
  }

  renderControls() {
    const { statusFilter, licenseFilter } = this.state;
    const statusOptions = [
      {
        label: 'All Users',
        value: null,
        id: '1',
      },
      {
        label: 'Connected Users',
        value: true,
        id: '2',
      },
      {
        label: 'Not Connected Users',
        value: false,
        id: '3',
      },
    ];
    const licenseOptions = [
      {
        label: 'Any License',
        value: null,
        id: '1',
      },
      {
        label: 'Active License',
        value: true,
        id: '2',
      },
      {
        label: 'Inactive License',
        value: false,
        id: '3',
      },
    ];
    const activeStatus = find(statusOptions, { value: statusFilter }) || first(statusOptions);
    const activeLicense = find(licenseOptions, { value: licenseFilter }) || first(statusOptions);

    return (
      <span className='sub-header-with-controls'>
        Showing{' '}
        <Dropdown id='status-dropdown'>
          <Dropdown.Toggle bsSize='small'>{activeStatus.label}</Dropdown.Toggle>
          <Dropdown.Menu>
            {statusOptions.map((option) => (
              <MenuItem
                key={option.id}
                active={option.value === statusFilter}
                onSelect={() => {
                  this.setStatus(option.value);
                }}>
                {option.label}
              </MenuItem>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        {' with '}
        <Dropdown id='license-dropdown'>
          <Dropdown.Toggle bsSize='small'>
            {activeLicense.label}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {licenseOptions.map((option) => (
              <MenuItem
                key={option.id}
                active={option.value === licenseFilter}
                onSelect={() => {
                  this.setLicense(option.value);
                }}>
                {option.label}
              </MenuItem>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </span>
    );
  }

  renderFullScreenToggle() {
    const { fullScreenMode } = this.state;
    const description = fullScreenMode
      ? 'Exit Full Screen'
      : 'Enter Full Screen';
    return (
      <span>
        &nbsp;&nbsp;
        <OverlayTrigger
          placement={fullScreenMode ? 'bottom' : 'top'}
          overlay={<Tooltip id='full-screen-tip'>{description}</Tooltip>}>
          <Button bsStyle='link' onClick={this.toggleFullScreenMode}>
            <Fa name={fullScreenMode ? 'compress' : 'expand'} />
          </Button>
        </OverlayTrigger>
      </span>
    );
  }

  renderDownloadButton() {
    if (!this.gridApi || this.props.disableDownload) {
      return null;
    }
    return (
      <span>
        &nbsp;&nbsp;
        <OverlayTrigger
          placement='top'
          overlay={
            <Tooltip id='custom-field-download-tip'>
              Export as Excel File
            </Tooltip>
          }>
          <Button bsStyle='link' onClick={this.handleDownload}>
            <Fa name='download' />
          </Button>
        </OverlayTrigger>
      </span>
    );
  }

  renderSearchForm() {
    const searchFormDefaultCriteria = {
      type: 'groupAND',
      values: [],
    };

    return (
      <SearchForm
        onLoadError={this.handleSearchFormLoadError}
        onSearchChange={this.handleSearchFormChange}
        defaultCriteria={searchFormDefaultCriteria}
        pullRight
        disabledRootLogic={false}
        disabled={false}
        type='teams/assign-team-members'
        loadRecommendedCriteria={false}
        requireTeamSelector={false}
        standalone={false}
        customFooterControls={null}
        dropup={true}
        readOnly={false}
        isDynamic
        startingNode='User'
        source="sf_user"
        showTeamSelector={false}
        hideLinkButton
      />
    );
  }

  renderUserTable() {
    const {
      organization,
      showOrganizationRoleColumn,
      showOrganizationDetailColumn,
      selectable,
      checkboxSelection,
      hideActionColumn,
      showFeedbackRequestControl,
      showEmailSignatures,
      showDevice,
      showBrowser,
      showLoginCount,
      height,
      viewableDimensions,
    } = this.props;
    const { topBarHeight, fullScreenMode } = this.state;

    let isRowSelectable = () => false;
    if (selectable) {
      if (checkboxSelection) {
        isRowSelectable = checkboxSelection;
      } else {
        isRowSelectable = () => true;
      }
    }

    const tHeight = height || viewableDimensions.height;
    const tableHeight = fullScreenMode
      ? Math.max(tHeight - topBarHeight, 100)
      : tHeight;

    const columnDefs = [
      {
        headerName: 'User',
        pinned: 'left',
        colId: 'SF',
        field: 'is_none_standard_user',
        sortable: false,
        resizable: true,
        width: 250,
        suppressMovable: true,
        lockPosition: true,
        lockVisible: true,
        lockPinned: true,
        enableRowGroup: false,
        checkboxSelection: selectable ? (checkboxSelection || true) : false,
        /* Not supported for Server-Side Row Model - MW 2023-09-06
        headerCheckboxSelection: selectable,
        headerCheckboxSelectionFilteredOnly: true,
        */
        valueGetter: (params) => {
          if (!params || !params.data) {
            return null;
          }
          return params.data.name || params.data.display_name;
        },
        cellStyle: (params) => {
          if (
            selectable &&
            checkboxSelection !== null &&
            !checkboxSelection(params)
          ) {
            return { paddingLeft: '17px' }; // make the unselectable rows align
          }
          return {};
        },
        cellRendererSelector: this.nameColumnCellRendererSelector,
        headerComponentParams: {
          showCheckbox: selectable,
          onSelectAll: this.onSelectedAllCheckbox,
          indeterminate: this.props.indeterminate(),
          selectedAll: this.state.selectedAll,
        },
      },
      {
        headerName: 'Active License',
        colId: 'LICENSE',
        width: 100,
        filter: false,
        sortable: true,
        resizable: true,
        enableRowGroup: false,
        cellRenderer: LicenseRenderer,
        valueGetter: (params) => {
          if (!params || !params.data) {
            return null;
          }
          if (params.data.is_none_standard_user === true) {
            return null;
          }
          if (params.data.user_id && params.data.active) {
            return true;
          }
          return false;
        },
      },
      ...(showOrganizationDetailColumn
        ? [
            {
              headerName: 'Organization',
              colId: 'ORGANIZATION',
              enableRowGroup: false,
              sortable: true,
              resizable: true,
              width: 200,
              cellRenderer: OrganizationNameRenderer,
              cellRendererParams: {
                // detailUrl: '/admin/organizations/',
              },
              valueGetter: (params) => {
                if (!params || !params.data) {
                  return null;
                }
                return params.data.organization
                  ? params.data.organization.display_name ||
                      params.data.organization.name
                  : null;
              },
            },
          ]
        : []),
      ...(showOrganizationRoleColumn
        ? [
            {
              headerName: 'Organization Role',
              colId: 'ROLE',
              // filter: 'agTextColumnFilter',
              sortable: true,
              resizable: true,
              enableRowGroup: false,
              width: 200,
              valueGetter: (params) => {
                if (!params || !params.data) {
                  return null;
                }
                return organizationRoles[params.data.organization_role];
              },
            },
          ]
        : []),
      {
        headerName: 'CRM ID',
        colId: 'SF_ID',
        filter: 'agTextColumnFilter',
        enableRowGroup: false,
        sortable: false,
        resizable: true,
        width: 100,
        field: 'user_id',
        cellRendererSelector: this.userIdColumnCellRendererSelector,
      },
      {
        headerName: 'CRM Role',
        colId: 'SF_ROLE',
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true,
        width: 200,
        field: 'user_role_name',
      },
      {
        headerName: 'CRM Profile',
        colId: 'SF_PROFILE',
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true,
        width: 200,
        field: 'profile_name',
      },
      {
        headerName: 'Email',
        colId: 'EMAIL',
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true,
        width: 100,
        field: 'email',
      },
      {
        headerName: 'Last Login',
        colId: 'LAST_LOGIN',
        field: 'last_activity',
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: [
            'equals',
            'notEqual',
            'lessThan',
            'lessThanOrEqual',
            'greaterThan',
            'greaterThanOrEqual',
          ],
          // suppressAndOrCondition: true,
        },
        sortable: true,
        resizable: true,
        width: 200,
        cellRendererSelector: this.lastLoginColumnCellRendererSelector,
      },
      ...(showLoginCount ? [{
        headerName: 'Login Count',
        type: 'numericColumn',
        colId: 'LOGIN_COUNT',
        field: 'logs',
        sortable: true,
        resizable: true,
        width: 150,
        cellRendererSelector: this.loginCountColumnCellRendererSelector,
      }] : []),
      {
        headerName: 'Username',
        colId: 'USERNAME',
        field: 'username',
        sortable: false,
        resizable: true,
        width: 150,
      },
      {
        headerName: 'Is Integration User',
        colId: 'SYNC',
        field: 'is_synchronization_user',
        width: 150,
        sortable: false,
        resizable: true,
        cellRenderer: IntegrationUserRenderer,
        valueGetter: this.isIntegrationUserGetter,
        cellRendererParams: {
          requestCheckUserPermission: (u) => {
            this.setState({ userCheckSyncPermission: u, showCheckSyncPermissionModal: true });
          },
        },
      },
      ...(showDevice ? [{
        headerName: 'Device',
        colId: 'DEVICE',
        width: 150,
        sortable: false,
        resizable: true,
        valueGetter: this.getDeviceValue,
        cellRenderer: DeviceRenderer,
      }] : []),
      ...(showBrowser ? [{
        headerName: 'Browser',
        colId: 'BROWSER',
        width: 150,
        sortable: false,
        resizable: true,
        valueGetter: this.getBrowserValue,
        cellRenderer: BrowserRenderer,
      }] : []),
      ...(showFeedbackRequestControl
        ? [
            {
              headerName: 'Disabled Feedback Request',
              colId: 'FEEDBACK_REQUEST_CONTROL',
              field: 'disable_feedback_request',
              pinned: 'right',
              sortable: false,
              resizable: true,
              width: 150,
              suppressMovable: true,
              lockPosition: true,
              lockVisible: true,
              lockPinned: true,
              cellRendererSelector: this
                .feedbackRequestColumnCellRendererSelector,
              cellRendererParams: {
                column: {
                  description: 'Disable Feedback Request Automations',
                },
                onUpdate: this.handleUserUpdated,
                organization,
              },
            },
          ]
        : []),
      ...(showEmailSignatures
        ? [
            {
              headerName: 'Email Authorization',
              colId: 'EMAIL_AUTHORIZATION',
              field: 'email',
              sortable: true,
              resizable: true,
              width: 120,
              valueGetter: this.emailSignatureColumnValueGetter,
              cellRenderer: EmailSignatureRenderer,
              cellRendererParams: {
                organization,
                onSignatureSent: this.handleSignatureSent,
              },
            },
          ]
        : []),
      ...(hideActionColumn
        ? []
        : [
            {
              headerName: 'Actions',
              colId: 'ACTIONS',
              field: '_id',
              pinned: 'right',
              sortable: false,
              resizable: true,
              width: 120,
              lockPosition: true,
              lockVisible: true,
              lockPinned: true,
              suppressMovable: true,
              valueGetter: this.actionColumnValueGetter,
              cellRendererSelector: this.actionColumnCellRendererSelector,
            },
          ]),
    ];

    return (
      <div className='ag-theme-alpine' style={{ width: '100%', height: tableHeight }}>
        <AgGridReact
          reactNext
          suppressColumnVirtualisation
          columnDefs={columnDefs}
          getRowId={(params) => params.data._id}
          onGridReady={this.onGridReady}
          onFirstDataRendered={this.onFirstDataRendered}
          rowSelection={selectable ? 'multiple' : null}
          onSelectionChanged={this.onSelectionChanged}
          singleClickEdit={selectable}
          suppressRowClickSelection
          deltaRowDataMode
          getRowClass={this.getRowClass}
          isRowSelectable={isRowSelectable}
          rowModelType='serverSide'
          defaultColDef={{
            menuTabs: [
              'generalMenuTab',
              'filterMenuTab',
              'columnsMenuTab',
            ],
          }}
          pagination
          paginationAutoPageSize
          components={{
            agColumnHeader: CustomHeader,
          }}
        />
      </div>
    );
  }

  render() {
    const {
      organization,
      topControl,
      canAddUser,
      disableShowFullScreenMode,
      disableDownload,
      showSearchForm,
    } = this.props;
    const {
      editingUser,
      fullScreenMode,
      showUserTable,
    } = this.state;

    const disableAddUser = !canAddUser || organization === null || organization === undefined;

    return (
      <div ref={(el) => { this._el = el }}>
        <Veil anchorElement={this._el} fullScreenMode={fullScreenMode}>
          <div>
            {this.renderModal()}
            {this.renderCheckPermissionModal()}
            <div style={{ height: 0 }} className='invisible'>
              {editingUser !== null &&
                (editingUser.organization || organization) && (
                  <OrganizationUserButton
                    user={editingUser}
                    showModal
                    onUpdate={this.handleUserUpdated}
                    onClose={() => {
                      this.setState({ editingUser: null });
                    }}
                    organization={editingUser.organization || organization}
                  />
                )}
            </div>
            <nav className='navbar navbar-default navbar-controls'>
              <form className='navbar-form navbar-left'>
                <SearchField collapsible={false} onSearch={this.handleSearchFieldChange} />
                {this.renderControls()}
              </form>
              <div className='navbar-form navbar-right' style={{ paddingRight: 30 }}>
                {topControl}
                {showSearchForm && this.renderSearchForm()}
                {!disableAddUser && (
                  <OrganizationUserButton
                    user={this.props.newUser}
                    organization={organization}
                    onAdd={this.handleUserAdded}
                  />
                )}
                {!disableDownload && this.renderDownloadButton()}
                {!disableShowFullScreenMode && this.renderFullScreenToggle()}
              </div>
              <ReactResizeDetector handleHeight onResize={this.onResizeTopBar} />
            </nav>
            {showUserTable && this.renderUserTable()}
            {!showUserTable && 
              <div className="text-muted text-right" style={{ padding: 10, border: "1px solid #d3d3d3" }}>
                Apply filters to see users <Fa name="arrow-up" />
              </div>
            }
          </div>
        </Veil>
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state, ownProps) {
  let organizationTypeName = 'CRM';
  if (ownProps.organization) {
    if (ownProps.organization.type === 'azure') {
      organizationTypeName = 'Microsoft Dynamics';
    } else if (ownProps.organization.type === 'force') {
      organizationTypeName = 'Salesforce';
    } else if (ownProps.organization.type === 'hubspot') {
      organizationTypeName = 'Hubspot';
    }
  }
  return {
    viewableDimensions: getViewableDimensions(state),
    loginUser: getUser(state),
    isSuperAdmin: getIsSuperAdmin(state),
    isAdmin: getIsAdmin(state),
    isPIUser: getIsPIUser(state),
    isOrganizationAdmin: getIsOrganizationAdmin(state),
    emailSignatures: getEmailSignatures(state),
    organizationTypeName,
  };
}

UserTable.propTypes = {
  dispatch: PropTypes.func.isRequired,
  loginUser: PropTypes.shape({
    _id: PropTypes.string,
    $token: PropTypes.string,
    organization__id: PropTypes.string,
  }).isRequired,
  organization: PropTypes.shape({
    _id: PropTypes.string,
    type: PropTypes.string,
  }),
  isSuperAdmin: PropTypes.bool.isRequired,
  showOrganizationRoleColumn: PropTypes.bool,
  showOrganizationDetailColumn: PropTypes.bool,
  showLoginCount: PropTypes.bool,
  showEmailColumn: PropTypes.bool,
  showHijack: PropTypes.bool,
  isAdmin: PropTypes.bool.isRequired,
  detailUrl: PropTypes.string,
  isPIUser: PropTypes.bool.isRequired,
  isOrganizationAdmin: PropTypes.bool.isRequired,
  onDelete: PropTypes.func,
  onUpdate: PropTypes.func,
  onAdd: PropTypes.func,
  onInvite: PropTypes.func,
  toggleSelect: PropTypes.func,
  width: PropTypes.number,
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  viewableDimensions: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number,
  }).isRequired,
  newUser: PropTypes.shape({}),
  topControl: PropTypes.node,
  selectable: PropTypes.bool,
  checkboxSelection: PropTypes.func,
  flashRow: PropTypes.shape({
    _id: PropTypes.string,
  }),
  fetchAction: PropTypes.func.isRequired,
  canAddUser: PropTypes.bool,
  statusFilter: PropTypes.bool,
  licenseFilter: PropTypes.bool,
  hideActionColumn: PropTypes.bool,
  showFeedbackRequestControl: PropTypes.bool,
  showEmailSignatures: PropTypes.bool,
  emailSignatures: PropTypes.shape({}),
  height: PropTypes.number,
  filter: PropTypes.shape({}),
  showDevice: PropTypes.bool,
  showBrowser: PropTypes.bool,
  onSelectedAllCheckbox: PropTypes.func,
  disableShowFullScreenMode: PropTypes.bool,
  disableDownload: PropTypes.bool,
  indeterminate: PropTypes.func,
  organizationTypeName: PropTypes.string,
  showSearchForm: PropTypes.bool,
};
UserTable.defaultProps = {
  detailUrl: null,
  showOrganizationRoleColumn: true,
  showOrganizationDetailColumn: true,
  showLoginCount: true,
  showEmailColumn: true,
  showHijack: false,
  selectedIds: [],
  onDelete: () => {},
  onUpdate: () => {},
  onInvite: () => {},
  toggleSelect: () => {},
  onAdd: () => {},
  newUser: {
    role: 'user',
    organization_role: 'user',
  },
  topControl: null,
  selectable: false,
  checkboxSelection: null,
  flashRow: null,
  canAddUser: true,
  statusFilter: null,
  licenseFilter: null,
  hideActionColumn: false,
  showFeedbackRequestControl: false,
  showEmailSignatures: false,
  emailSignatures: {},
  height: null,
  filter: {},
  showDevice: true,
  showBrowser: true,
  onSelectedAllCheckbox: () => {},
  disableShowFullScreenMode: false,
  disableDownload: false,
  indeterminate: () => false,
  organizationTypeName: 'CRM',
  showSearchForm: false,
};
UserTable.contextTypes = {
  router: PropTypes.shape({}),
  addNotification: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(UserTable);
